import { useLayoutEffect, useRef, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';

/**
 * Сегментний контрол зі скляним повзунком.
 *
 * Кнопки лишаються звичайними кнопками з класом `on` у вибраної; повзунок —
 * `::after` контейнера, якому сюди пишуться `--thumb-x` / `--thumb-w` за
 * вимірами вибраної кнопки. Колір повзунка береться з її інлайнових
 * `--tool-bg` / `--tool-fg` (палітра позначок), інакше — скло за замовчуванням.
 *
 * Переїзд повзунка анімує CSS-transition; тут лише короткий «присід» по
 * ширині в момент зміни — як у Liquid Glass Switcher.
 */
export function Segments({
  children,
  onClass = 'on',
  ...rest
}: HTMLAttributes<HTMLDivElement> & { children: ReactNode; onClass?: string }): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const prevX = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void => {
      const on = el.querySelector<HTMLElement>(`:scope > .${onClass}`);
      if (!on) {
        el.removeAttribute('data-thumb');
        prevX.current = null;
        return;
      }
      const x = on.offsetLeft;
      const w = on.offsetWidth;
      el.style.setProperty('--thumb-x', `${x}px`);
      el.style.setProperty('--thumb-w', `${w}px`);
      el.style.setProperty('--thumb-bg', on.style.getPropertyValue('--tool-bg') || '');
      el.style.setProperty('--thumb-fg', on.style.getPropertyValue('--tool-fg') || '');
      if (!el.hasAttribute('data-thumb')) {
        // Перша поява — без переїзду з нуля: повзунок просто стоїть на місці.
        el.setAttribute('data-thumb', 'still');
        requestAnimationFrame(() => el.setAttribute('data-thumb', ''));
      } else if (prevX.current !== null && prevX.current !== x && typeof el.animate === 'function') {
        try {
          el.animate(
            [{ scale: '1 1' }, { scale: '1.12 1' }, { scale: '1 1' }],
            { duration: 280, easing: 'ease', pseudoElement: '::after' },
          );
        } catch {
          // Старий WebKit без pseudoElement в animate — просто без присіду.
        }
      }
      prevX.current = x;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });

  return (
    <div ref={ref} {...rest}>
      {children}
    </div>
  );
}

export type { CSSProperties };
